/**
 * 租户配置（租户级参数 / OCR 字段模板 / 消息提醒规则）
 *
 * 这里的每一项都会直接改变系统的行为，因此页面遵循两个原则：
 *   1. 每个开关旁边写清「改了会发生什么」，而不是只给一个名字；
 *   2. 保存前给出差异预览（哪些项被改了、从什么改成什么），
 *      因为租户配置没有版本回滚，改错了只能再改回来。
 *
 * 特别提示：本页不含「自动生成凭证」这类配置——凭证由财务在 T+ 端生成，
 * 本平台没有这个能力，也不应提供这样的开关。
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Slider,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ExperimentOutlined, ReloadOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import {
  MESSAGE_CHANNEL_LABELS,
  MessageChannel,
  PermissionCode,
  RISK_LEVEL_LABELS,
  RiskLevel,
  type NotifyRules,
  type OcrFieldTemplate,
  type TenantConfig,
} from '@ilc/shared';
import { tenantApi } from '@/api/endpoints';
import { mockTenantConfigData } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

/** OCR 可识别字段清单（对应 shared 的 OcrFields） */
const OCR_FIELD_OPTIONS: Array<{ value: string; label: string; group: string }> = [
  { value: 'invoiceCode', label: '发票代码', group: '票面标识' },
  { value: 'invoiceNumber', label: '发票号码', group: '票面标识' },
  { value: 'invoiceDate', label: '开票日期', group: '票面标识' },
  { value: 'machineCode', label: '机器编号', group: '票面标识' },
  { value: 'checkCode', label: '校验码', group: '票面标识' },
  { value: 'passwordArea', label: '密码区', group: '票面标识' },
  { value: 'invoiceType', label: '发票类型（中文）', group: '票面标识' },
  { value: 'purchaserName', label: '购买方名称', group: '购买方' },
  { value: 'purchaserTaxNumber', label: '购买方税号', group: '购买方' },
  { value: 'purchaserContactInfo', label: '购买方地址电话', group: '购买方' },
  { value: 'purchaserBankAccountInfo', label: '购买方开户行及账号', group: '购买方' },
  { value: 'sellerName', label: '销售方名称', group: '销售方' },
  { value: 'sellerTaxNumber', label: '销售方税号', group: '销售方' },
  { value: 'sellerContactInfo', label: '销售方地址电话', group: '销售方' },
  { value: 'sellerBankAccountInfo', label: '销售方开户行及账号', group: '销售方' },
  { value: 'invoiceAmountPreTax', label: '不含税金额', group: '金额' },
  { value: 'invoiceTax', label: '税额', group: '金额' },
  { value: 'totalAmount', label: '价税合计', group: '金额' },
  { value: 'totalAmountInWords', label: '价税合计（大写）', group: '金额' },
  { value: 'recipient', label: '收款人', group: '其它' },
  { value: 'reviewer', label: '复核人', group: '其它' },
  { value: 'drawer', label: '开票人', group: '其它' },
  { value: 'remarks', label: '备注', group: '其它' },
  { value: 'title', label: '标题', group: '其它' },
];

const OCR_FIELD_LABELS: Record<string, string> = Object.fromEntries(
  OCR_FIELD_OPTIONS.map((f) => [f.value, f.label]),
);

interface ConfigFormValues {
  defaultAccountSet?: string;
  invoiceNoPrefix?: string;
  autoSubmitAfterUpload?: boolean;
  taskTimeoutHours?: number;
  ocrFields?: string[];
  ocrRequiredFields?: string[];
  ocrMinConfidence?: number;
  notifyTimeoutHours?: number;
  timeoutChannels?: MessageChannel[];
  notifyRisk?: boolean;
  riskNotifyMinLevel?: RiskLevel;
  notifyProviderError?: boolean;
}

function toFormValues(config: TenantConfig): ConfigFormValues {
  const configs = config.configs ?? {};
  const ocr: OcrFieldTemplate | null = config.ocrFieldTemplate;
  const notify: NotifyRules | null = config.notifyRules;
  return {
    defaultAccountSet: typeof configs.defaultAccountSet === 'string' ? configs.defaultAccountSet : '',
    invoiceNoPrefix: typeof configs.invoiceNoPrefix === 'string' ? configs.invoiceNoPrefix : '',
    autoSubmitAfterUpload:
      typeof configs.autoSubmitAfterUpload === 'boolean' ? configs.autoSubmitAfterUpload : false,
    taskTimeoutHours: typeof configs.taskTimeoutHours === 'number' ? configs.taskTimeoutHours : 24,
    ocrFields: ocr?.fields ?? OCR_FIELD_OPTIONS.map((f) => f.value),
    ocrRequiredFields: ocr?.requiredFields ?? [],
    ocrMinConfidence: ocr?.minConfidence ?? 85,
    notifyTimeoutHours: notify?.taskTimeoutHours ?? 24,
    timeoutChannels: notify?.timeoutChannels ?? [MessageChannel.IN_APP],
    notifyRisk: notify?.notifyRisk ?? true,
    riskNotifyMinLevel: notify?.riskNotifyMinLevel ?? RiskLevel.MEDIUM,
    notifyProviderError: notify?.notifyProviderError ?? true,
  };
}

export default function TenantConfigPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.TENANT_CONFIG_WRITE);
  const [form] = Form.useForm<ConfigFormValues>();
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const config = useResource<TenantConfig>(() => tenantApi.config(), {
    fallback: () => mockTenantConfigData(),
  });

  const initial = useMemo(
    () => (config.data ? toFormValues(config.data) : null),
    [config.data],
  );

  useEffect(() => {
    if (initial) {
      form.setFieldsValue(initial);
      setDirty(false);
    }
  }, [initial, form]);

  const watchAll = Form.useWatch([], form);
  const currentValues = (watchAll ?? initial ?? {}) as ConfigFormValues;

  /** 差异清单：让管理员在保存前确认到底改了什么 */
  const diff = useMemo(() => {
    if (!initial) return [] as Array<{ item: string; before: string; after: string }>;
    const rows: Array<{ item: string; before: string; after: string }> = [];
    const show = (v: unknown): string => {
      if (v === undefined || v === null || v === '') return '（空）';
      if (typeof v === 'boolean') return v ? '开启' : '关闭';
      if (Array.isArray(v)) return v.length ? v.join('、') : '（无）';
      return String(v);
    };
    const labels: Record<keyof ConfigFormValues, string> = {
      defaultAccountSet: '默认账套',
      invoiceNoPrefix: '内部单号前缀',
      autoSubmitAfterUpload: '上传后自动提交校验',
      taskTimeoutHours: '任务超时（小时）',
      ocrFields: 'OCR 识别字段',
      ocrRequiredFields: 'OCR 必填字段',
      ocrMinConfidence: '最低置信度',
      notifyTimeoutHours: '超时提醒（小时）',
      timeoutChannels: '超时提醒渠道',
      notifyRisk: '推送风险事件',
      riskNotifyMinLevel: '风险推送最低等级',
      notifyProviderError: '推送接口异常',
    };
    (Object.keys(labels) as Array<keyof ConfigFormValues>).forEach((key) => {
      const before = show(initial[key]);
      const after = show(currentValues[key]);
      if (before !== after) rows.push({ item: labels[key], before, after });
    });
    return rows;
  }, [initial, currentValues]);

  const save = async (): Promise<void> => {
    const values = await form.validateFields();
    setSaving(true);
    const result = await performAction({
      action: () =>
        tenantApi.updateConfig({
          configs: {
            defaultAccountSet: values.defaultAccountSet ?? '',
            invoiceNoPrefix: values.invoiceNoPrefix ?? '',
            autoSubmitAfterUpload: Boolean(values.autoSubmitAfterUpload),
            taskTimeoutHours: Number(values.taskTimeoutHours ?? 24),
          },
          ocrFieldTemplate: {
            fields: values.ocrFields ?? [],
            requiredFields: values.ocrRequiredFields ?? [],
            minConfidence: Number(values.ocrMinConfidence ?? 85),
          },
          notifyRules: {
            taskTimeoutHours: Number(values.notifyTimeoutHours ?? 24),
            timeoutChannels: values.timeoutChannels ?? [],
            notifyRisk: Boolean(values.notifyRisk),
            riskNotifyMinLevel: values.riskNotifyMinLevel ?? RiskLevel.MEDIUM,
            notifyProviderError: Boolean(values.notifyProviderError),
          },
        }),
      successMessage: '租户配置已保存，新的发票与流程将按新配置执行（在办发票不受影响）',
      offline: () => undefined,
      offlineMessage: '后端配置接口尚未就绪（/api/tenant/config 返回 404），本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setDirty(false);
      config.reload();
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="租户配置"
        description="租户级运行参数、OCR 识别字段模板与消息提醒规则。配置按租户隔离，不影响其它租户。"
        tags={<Tag icon={<SettingOutlined />} color="blue">租户级</Tag>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={config.reload}>
              重新加载
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!canWrite || !dirty}
              onClick={() => void save()}
            >
              保存配置
            </Button>
          </Space>
        }
      />

      {!canWrite ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前账号为只读"
          description="您没有 tenant:config:write 权限，可以查看配置但不能修改。如需变更请联系租户管理员或技术实施组。"
        />
      ) : null}

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="本平台不生成会计凭证"
        description="租户配置里没有「自动生成凭证」这类开关：推送 T+ 只把业务数据放入待办业务池，会计科目与凭证由财务在 T+ 端选择生成，本平台仅同步业务数据。"
      />

      <PageState loading={config.loading} error={config.error} onRetry={config.reload} skeleton rows={8}>
        <Form<ConfigFormValues>
          form={form}
          layout="vertical"
          initialValues={initial ?? undefined}
          onValuesChange={() => setDirty(true)}
          disabled={!canWrite}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <Card size="small" title="基础参数">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="defaultAccountSet"
                      label="默认账套"
                      extra="推送 T+ 时使用的账套名称，需与 T+ 端账套一致"
                    >
                      <Input placeholder="如 演示科技-默认账套" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="invoiceNoPrefix"
                      label="内部单号前缀"
                      rules={[{ pattern: /^[A-Za-z]{0,8}$/, message: '仅可包含字母，最多 8 位' }]}
                      extra="业务单号形如 BZ20260910000101"
                    >
                      <Input className="ilc-mono" placeholder="BZ" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="autoSubmitAfterUpload"
                      label="上传后自动提交校验"
                      valuePropName="checked"
                      extra="开启后经办人上传即进入校验与待初审队列；关闭则由经办人手动提交"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="taskTimeoutHours"
                      label="任务默认超时（小时）"
                      extra="流程节点未单独设置超时时间时使用该值"
                    >
                      <InputNumber style={{ width: '100%' }} min={1} max={720} />
                    </Form.Item>
                  </Col>
                </Row>
                <Alert
                  type="info"
                  showIcon
                  message="自动提交不等于自动通过"
                  description="无论是否开启自动提交，发票都必须经过财务人员人工初审才能推送 T+。自动化只负责识别与风险标记。"
                />
              </Card>
            </Col>

            <Col xs={24} xl={12}>
              <Card size="small" title="OCR 识别字段模板">
                <Form.Item
                  name="ocrFields"
                  label="需要识别的字段"
                  extra="字段越多，识别耗时与调用成本越高；建议只保留业务真正使用的字段"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="选择字段"
                    optionFilterProp="label"
                    options={OCR_FIELD_OPTIONS.map((f) => ({
                      value: f.value,
                      label: `${f.group} · ${f.label}`,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="ocrRequiredFields"
                  label="必填字段"
                  extra="缺失将产生「字段缺失」低风险项（提示，不阻断流程）"
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="从已选字段中选择"
                    optionFilterProp="label"
                    options={(currentValues.ocrFields ?? []).map((f) => ({
                      value: f,
                      label: OCR_FIELD_LABELS[f] ?? f,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="ocrMinConfidence"
                  label="最低识别置信度"
                  extra="低于该值产生「识别置信度低」风险项，需人工核对票面"
                >
                  <Slider min={50} max={99} marks={{ 50: '50', 75: '75', 85: '85', 99: '99' }} />
                </Form.Item>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                  校验要素口径（发票代码是否必填、金额按价税合计还是不含税金额、校验码取后 6 位等）由票种决定，
                  在「发票上传」页可逐票种查看，不在此处配置。
                </Typography.Paragraph>
              </Card>
            </Col>

            <Col xs={24} xl={12}>
              <Card size="small" title="消息与提醒规则">
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="notifyTimeoutHours" label="超时提醒（小时）" extra="任务停留超过该时长提醒处理人">
                      <InputNumber style={{ width: '100%' }} min={1} max={720} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="timeoutChannels" label="超时提醒渠道">
                      <Select
                        mode="multiple"
                        options={Object.entries(MESSAGE_CHANNEL_LABELS).map(([value, label]) => ({
                          value,
                          label,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="notifyRisk" label="推送风险事件" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="riskNotifyMinLevel"
                      label="风险推送的最低等级"
                      extra="低于该等级的风险不单独推送，避免消息过载"
                    >
                      <Select
                        options={Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => ({ value, label }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="notifyProviderError"
                      label="推送接口异常"
                      valuePropName="checked"
                      extra="外部接口超时、熔断、配额耗尽时通知技术实施组"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Alert
                  type="info"
                  showIcon
                  message="高风险提示不看渠道设置"
                  description="高风险发票（如已冲红、已作废）的提示属于合规要求，始终以站内消息送达处理人，不受「风险推送的最低等级」限制。"
                />
              </Card>
            </Col>

            <Col xs={24} xl={12}>
              <Card size="small" title="保存前确认（与当前生效配置的差异）">
                {diff.length === 0 ? (
                  <Typography.Text type="secondary">尚未修改任何配置项。</Typography.Text>
                ) : (
                  <>
                    <Table
                      size="small"
                      rowKey="item"
                      pagination={false}
                      dataSource={diff}
                      columns={[
                        { title: '配置项', dataIndex: 'item', width: 150 },
                        {
                          title: '当前',
                          dataIndex: 'before',
                          render: (v: string) => <Typography.Text type="secondary">{v}</Typography.Text>,
                        },
                        { title: '保存后', dataIndex: 'after', render: (v: string) => <Tag color="blue">{v}</Tag> },
                      ]}
                    />
                    <Divider style={{ margin: '12px 0' }} />
                    <Space direction="vertical" size={4}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        <ExperimentOutlined /> 共 {diff.length} 项变更。保存后立即生效，作用于新上传的发票与新的流程任务；
                        已进入流程的发票沿用原配置。
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        配置变更会记入平台审计日志（分类：配置变更），请确认变更内容符合内部审批要求。
                      </Typography.Text>
                    </Space>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        </Form>
      </PageState>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        提示：接口凭证（验真、企查查、T+ 的 AppKey/Secret）属于敏感配置，请在「外部接口配置」页维护，
        本页不展示也不接受凭证类输入。
      </Typography.Paragraph>
    </div>
  );
}
